import { useState } from 'react';
import { BsArrowCounterclockwise } from 'react-icons/bs';
import { Check, ChevronDown } from 'lucide-react';
import { CopyableGridText, EmptyGridCell, GRID_HINT_TOOLTIP_CLASS, GridCellWrap } from '../../../components/shared/agGridCells';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import type { ColDef, ICellRendererParams } from '../../../components/ui/table';
import { Switch } from '../../../components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip';
import { cn } from '../../../lib/utils';
import type { RiskRule } from '../models/types';

const WINDOW_OPTIONS = [
  { value: 'HOUR', label: '/ hr' },
  { value: 'DAY', label: '/ day' },
] as const;

function WindowSelect({
  value,
  disabled,
  label,
  onChange,
}: {
  value: 'HOUR' | 'DAY';
  disabled: boolean;
  label: string;
  onChange: (value: 'HOUR' | 'DAY') => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={label}
          onPointerDown={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
          className="flex h-8 w-[5.25rem] shrink-0 items-center justify-between gap-1 rounded-md border border-input bg-muted/40 px-2 text-sm text-foreground shadow-sm outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:border-border disabled:bg-transparent disabled:opacity-50"
        >
          <span className="truncate">{value === 'DAY' ? '/ day' : '/ hr'}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="z-[200] w-[7.5rem] p-1"
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onPointerDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      >
        {WINDOW_OPTIONS.map((option) => {
          const selected = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={selected}
              className={cn(
                'relative flex w-full cursor-pointer items-center rounded-sm py-1.5 pl-2 pr-8 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground',
                selected && 'bg-accent/60',
              )}
              onPointerDown={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onChange(option.value);
                setOpen(false);
              }}
            >
              {option.label}
              {selected ? (
                <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
                  <Check className="h-4 w-4" />
                </span>
              ) : null}
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

function isTypingTarget(event: Event | null | undefined): boolean {
  const target = event?.target as HTMLElement | null;
  if (!target) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'BUTTON' || tag === 'TEXTAREA';
}

export type RiskRulesGridContext = {
  isDirty: (rule: RiskRule) => boolean;
  onWeightChange: (ruleType: string, weight: number) => void;
  onResetWeight: (rule: RiskRule) => void;
  onEnabledChange: (ruleType: string, enabled: boolean) => void;
  onThresholdChange: (ruleType: string, threshold: number) => void;
  onResetThreshold: (rule: RiskRule) => void;
  onWindowChange: (ruleType: string, window: 'HOUR' | 'DAY') => void;
};

function RuleCellRenderer(params: ICellRendererParams<RiskRule>) {
  const rule = params.data;
  if (!rule) return <EmptyGridCell />;

  return (
    <GridCellWrap>
      <CopyableGridText
        text={rule.label}
        hint={rule.label}
        copyLabel="Copy rule"
        className="w-auto min-w-0"
        textClassName={cn(
          'text-[13px] font-medium',
          rule.enabled ? 'text-foreground' : 'text-muted-foreground',
        )}
      />
      {rule.hardRule ? (
        <span className="inline-flex shrink-0 items-center rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
          Limit rule
        </span>
      ) : null}
    </GridCellWrap>
  );
}

function WeightCellRenderer(params: ICellRendererParams<RiskRule>) {
  const rule = params.data;
  const context = params.context as RiskRulesGridContext | undefined;
  if (!rule || !context) return <EmptyGridCell />;

  const atDefault = rule.weight === rule.defaultWeight;

  return (
    <GridCellWrap>
      <input
        type="number"
        min={0}
        max={1000}
        value={rule.weight}
        disabled={!rule.enabled}
        aria-label={`Score weight for ${rule.label}`}
        onPointerDown={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        onChange={(event) => context.onWeightChange(rule.ruleType, Number(event.target.value))}
        className="h-8 w-20 shrink-0 rounded-md border border-input bg-muted/40 px-2 text-sm tabular-nums text-foreground shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:border-border disabled:bg-transparent disabled:opacity-50"
      />
      {atDefault ? (
        <span className="text-[11px] text-muted-foreground">default</span>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={`Reset to default (${rule.defaultWeight})`}
              onMouseDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                context.onResetWeight(rule);
              }}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-muted"
            >
              <BsArrowCounterclockwise className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className={GRID_HINT_TOOLTIP_CLASS}>
            Reset to default ({rule.defaultWeight})
          </TooltipContent>
        </Tooltip>
      )}
    </GridCellWrap>
  );
}

function ThresholdCellRenderer(params: ICellRendererParams<RiskRule>) {
  const rule = params.data;
  const context = params.context as RiskRulesGridContext | undefined;
  if (!rule || !context) return <EmptyGridCell />;

  // Non-velocity rules are plain conditions with no limit to tune.
  if (rule.thresholdKind === 'NONE') {
    return (
      <GridCellWrap>
        <span className="text-[13px] text-muted-foreground">—</span>
      </GridCellWrap>
    );
  }

  const isAmount = rule.thresholdKind === 'AMOUNT';
  const value = rule.threshold ?? rule.defaultThreshold ?? 0;
  const atDefault = (rule.threshold == null || rule.threshold === rule.defaultThreshold)
    && (rule.thresholdWindow ?? rule.defaultThresholdWindow) === rule.defaultThresholdWindow;
  const window = rule.thresholdWindow ?? rule.defaultThresholdWindow ?? 'HOUR';

  return (
    <GridCellWrap>
      <input
        type="number"
        min={isAmount ? 0 : 1}
        step={isAmount ? 'any' : 1}
        value={value}
        disabled={!rule.enabled}
        aria-label={`Limit for ${rule.label}`}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
        onChange={(event) => context.onThresholdChange(rule.ruleType, Number(event.target.value))}
        className="h-8 w-20 shrink-0 rounded-md border border-input bg-muted/40 px-2 text-sm tabular-nums text-foreground shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:border-border disabled:bg-transparent disabled:opacity-50"
      />
      <WindowSelect
        value={window}
        disabled={!rule.enabled}
        label={`Window for ${rule.label}`}
        onChange={(next) => context.onWindowChange(rule.ruleType, next)}
      />
      {atDefault ? (
        <span className="text-[11px] text-muted-foreground">default</span>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={`Reset to default (${rule.defaultThreshold})`}
              onMouseDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                context.onResetThreshold(rule);
              }}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-muted"
            >
              <BsArrowCounterclockwise className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className={GRID_HINT_TOOLTIP_CLASS}>
            Reset to default ({rule.defaultThreshold})
          </TooltipContent>
        </Tooltip>
      )}
    </GridCellWrap>
  );
}

function EnabledCellRenderer(params: ICellRendererParams<RiskRule>) {
  const rule = params.data;
  const context = params.context as RiskRulesGridContext | undefined;
  if (!rule || !context) return <EmptyGridCell />;

  return (
    <GridCellWrap className="justify-center">
      <Switch
        checked={rule.enabled}
        aria-label={`${rule.enabled ? 'Disable' : 'Enable'} ${rule.label}`}
        onCheckedChange={(value) => context.onEnabledChange(rule.ruleType, value === true)}
        onClick={(event) => event.stopPropagation()}
      />
    </GridCellWrap>
  );
}

export function createRiskRuleColumnDefs(): ColDef<RiskRule>[] {
  return [
    {
      headerName: 'Rule',
      field: 'label',
      colId: 'label',
      cellRenderer: RuleCellRenderer,
      flex: 1,
      minWidth: 240,
      cellClassRules: {
        'border-l-[3px] border-l-[#c69026]': (params) => {
          const context = params.context as RiskRulesGridContext | undefined;
          return Boolean(params.data && context?.isDirty(params.data));
        },
      },
    },
    {
      headerName: 'Score weight',
      colId: 'weight',
      // AG Grid only redraws a cell when this value changes. Enabled has to be
      // part of it — otherwise switching a rule on leaves the weight input
      // stuck disabled while the limit input and window dropdown refresh.
      valueGetter: (params) => {
        const rule = params.data;
        if (!rule) return '';
        return `${rule.weight}|${rule.enabled ? 1 : 0}`;
      },
      comparator: (_left, _right, nodeA, nodeB) =>
        (nodeA.data?.weight ?? 0) - (nodeB.data?.weight ?? 0),
      cellRenderer: WeightCellRenderer,
      width: 220,
      minWidth: 180,
      sortable: true,
      filter: false,
      suppressKeyboardEvent: (params) => isTypingTarget(params.event),
    },
    {
      headerName: 'Limit',
      colId: 'threshold',
      // The cell shows the limit and its window. AG Grid only redraws a cell when this
      // value changes, so the window has to be part of it — otherwise picking / day
      // while / hr is showing never refreshes the renderer.
      valueGetter: (params) => {
        const rule = params.data;
        if (!rule) return '';
        return `${rule.threshold ?? ''}|${rule.thresholdWindow ?? ''}|${rule.enabled ? 1 : 0}`;
      },
      comparator: (_left, _right, nodeA, nodeB) =>
        (nodeA.data?.threshold ?? 0) - (nodeB.data?.threshold ?? 0),
      cellRenderer: ThresholdCellRenderer,
      width: 290,
      minWidth: 250,
      sortable: true,
      filter: false,
      suppressKeyboardEvent: (params) => isTypingTarget(params.event),
    },
    {
      headerName: 'On',
      field: 'enabled',
      colId: 'enabled',
      cellRenderer: EnabledCellRenderer,
      width: 90,
      maxWidth: 110,
      sortable: true,
      filter: false,
      suppressKeyboardEvent: (params) => isTypingTarget(params.event),
    },
  ];
}
